import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import Pagination from "../../../components/ui/Pagination";
import {
  Button,
  EmptyStateHighlights,
  EmptyStateProps,
  Modal,
  Tabs,
  TabItem,
  buttonClasses,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
// Only for the two first-run states below — see `EmptyState`'s `welcome`
// variant. Not the rail's plan icon: that one is a 20px line mark.
import { ReactComponent as FirstPlanMark } from "../../../assets/svg/No data images/plan.svg";
import PlansTable from "./PlansTable";
import CreatePlanModal from "./CreatePlanModal";
import { summaryToPlanRow, toPlanSummary } from "../planMapping";
import {
  useCreatedPlans,
  useDeletedPlanIds,
  usePlans,
  useSessionPlans,
} from "../hooks/usePlans";
import { useMealPlans } from "../hooks/useMealPlans";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { activateMealPlan } from "../services/mealPlansService";
import { savePlan } from "../services/plansService";
import { ROUTES, planDetailPath } from "../../../routes/paths";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type { NewPlan, PlanRow, PlanTabId } from "../types/plans.types";

const PLANS_PER_PAGE = 10;

const TABS: TabItem<PlanTabId>[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
];

/** What plans are worth having, said the same way on both of the first-run
 *  states below: the two differ in which step is next, not in what the screen
 *  is for. */
const PLAN_HIGHLIGHTS: EmptyStateHighlights = {
  title: "With plans, you can",
  items: [
    "Build personalised meal plans for each client's goals.",
    "Share a branded PDF plan in a couple of taps.",
  ],
};

export interface MealPlansPanelProps {
  /**
   * Whether this workspace may still write plans, read once by the screen
   * above — see `CreatePlanPage`. Meal plans are one of the three things a
   * subscription pays for, so with none running every way into the builder
   * is off. The list itself stays readable.
   */
  gate: SubscriptionGate;
  /**
   * Whether the workspace has nobody to write a plan for. A plan is always a
   * plan *for* someone — the create dialog's first step is picking the client
   * — so with none there is nothing to offer here but the way to add one.
   *
   * Only a roster that came back empty counts; the screen above decides, so
   * that its other lists can read the same answer.
   */
  noClientsYet: boolean;
}

/**
 * The Meal list of the Create plan screen: every meal plan in the workspace,
 * filtered, searched and paged — the same table a client's Plans tab shows
 * for one person, listing everyone's.
 *
 * Creating one is a dialog rather than a screen: it asks the two things a
 * plan cannot be started without, and the builder it opens is where the plan
 * is actually written.
 *
 * Mounted only while Meal is the kind on screen — see `CreatePlanPage`. It
 * fetches a page of plans per filter, and a panel left mounted behind the
 * Supplement switch would go on doing that for a list nobody is looking at.
 */
const MealPlansPanel: React.FC<MealPlansPanelProps> = ({
  gate,
  noClientsYet,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  const { create, remove } = usePlans();

  const [activeTab, setActiveTab] = useState<PlanTabId>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PlanRow | null>(null);
  const [activating, setActivating] = useState<PlanRow | null>(null);
  const [activatingNow, setActivatingNow] = useState(false);

  // Every plan in the workspace — no `client`, which is what makes this the
  // whole list rather than one person's. The tab, the search box and the page
  // are all the server's, so changing any of them refetches.
  const {
    plans: fetched,
    count,
    status: loadStatus,
    error,
    refused,
  } = useMealPlans({
    status: activeTab === "draft" ? "draft" : "all",
    search,
    page,
    pageSize: PLANS_PER_PAGE,
  });

  // Plans made in this sitting, and plans edited in it. The first go on top of
  // the first page — the list was fetched before they existed — and the second
  // stand in for the row they were fetched as.
  const createdPlans = useCreatedPlans();
  const sessionPlans = useSessionPlans();
  const deletedIds = useDeletedPlanIds();

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();

    const editedById = new Map(
      sessionPlans.map(toPlanSummary).map((plan) => [plan.id, plan])
    );
    const gone = new Set(deletedIds);

    // An edited plan takes the place of the row it was fetched as, rather
    // than jumping the queue: only a plan the server has never seen is new,
    // and only a new plan belongs at the top.
    const remote = fetched
      .filter((plan) => !gone.has(plan.id))
      .map((plan) => editedById.get(plan.id) ?? plan);

    const fetchedIds = new Set(fetched.map((plan) => plan.id));

    // The same two filters the server was asked for, applied by hand to the
    // plans made here — this page of the list was fetched before them, so
    // nothing else would. Only on the first page: they belong above the
    // server's rows, and every later page is somewhere below.
    const created =
      page === 1
        ? createdPlans
            .map(toPlanSummary)
            .filter((plan) => !gone.has(plan.id) && !fetchedIds.has(plan.id))
            .filter((plan) => activeTab === "all" || plan.status === "draft")
            .filter(
              (plan) =>
                !query ||
                plan.name.toLowerCase().includes(query) ||
                plan.clientName.toLowerCase().includes(query) ||
                plan.planType.toLowerCase().includes(query)
            )
        : [];

    return [...created, ...remote].map(summaryToPlanRow);
  }, [createdPlans, sessionPlans, fetched, deletedIds, activeTab, search, page]);

  // The server's count, not the rows on screen: the table only ever holds one
  // page, so there is nothing here to divide.
  const totalPages = Math.max(1, Math.ceil(count / PLANS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PLANS_PER_PAGE;

  // And a filter that shrank the list past the page in hand lands on the last
  // one, rather than leaving the screen asking for a page that is no longer
  // there. Only once an answer has arrived: mid-load the count is the previous
  // filter's, and a failed load has no count at all.
  useEffect(() => {
    if (loadStatus === "ready" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  // A page that arrives quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  /**
   * The list with nothing written in it yet, and the next step being here: the
   * builder opens on the press rather than leading anywhere. Both the ordinary
   * first-run state and the refused one below draw it, since neither has a plan
   * to show and both are answered on this screen.
   *
   * `gate.props` is what makes the button explain itself to a workspace with no
   * subscription, which is the same workspace the refusal comes from.
   */
  const firstPlanState = (): EmptyStateProps => ({
    variant: "welcome",
    illustration: <FirstPlanMark aria-hidden className="w-11 h-11" />,
    title: "Create your first meal plan",
    description:
      "Build a plan once and send it to as many clients as you like - every plan you create is listed here.",
    highlights: PLAN_HIGHLIGHTS,
    action: (
      <Button
        size="md"
        startIcon={<Plus className="w-4 h-4" />}
        onClick={() => setCreating(true)}
        {...gate.props("create a plan")}
      >
        Create plan
      </Button>
    ),
  });

  // The failure comes first: an empty table under a "no plans yet" message
  // would read as an answer, when in fact nothing was ever heard back. Nothing
  // about loading — a page on its way says nothing and then shimmers its rows,
  // and the status line below says so in words.
  //
  // No retry offered either. What stops this list is usually the workspace's
  // subscription rather than a call that dropped, and pressing a button to ask
  // the same question again only gets the same answer back. The server's own
  // words below say what to do instead; reloading the page is the way to try.
  const emptyState = (): EmptyStateProps => {
    // A refusal is not a failure to draw. The server turns this read down when
    // there is no plan running, and a workspace that has never been able to
    // write a meal plan has never written one — so what the screen has to
    // report is an empty list, which is what the welcome says. The Create plan
    // button under it carries the reason for the press that comes anyway, and
    // Billing is where the plan is actually bought. See `useMealPlans`.
    //
    // Not the no-clients wording, however few clients came back: that read was
    // refused too, so whether this workspace has a roster is unknown.
    if (refused) return firstPlanState();

    if (loadStatus === "failed") {
      const state = failedEmptyState("We could not load your plans");
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("plans");

    // Before anything about plans: with no clients there is no plan to be
    // written, so "No plans created yet" beside a Create plan button would
    // send someone into a dialog whose first step they cannot answer. The
    // headline is still the plan, because that is what this screen is for and
    // what the reader came to do; the line under it is what names the real
    // first step. On both tabs — a workspace with no clients has no drafts
    // either, and the reason is the same one.
    if (noClientsYet)
      return {
        variant: "welcome",
        illustration: <FirstPlanMark aria-hidden className="w-11 h-11" />,
        title: "Create your first meal plan",
        description:
          "Every plan starts with a client. Add one to begin building tailored meal and nutrition plans.",
        highlights: PLAN_HIGHLIGHTS,
        // A link and not a button: this goes somewhere. See `buttonClasses`.
        //
        // Except where there is no plan behind the workspace, when there is
        // nowhere for it to go: adding a client is shut too, and a link cannot
        // be disabled or give a reason. It becomes the button that can.
        action: gate.locked ? (
          <Button
            size="md"
            startIcon={<Plus className="w-4 h-4" />}
            {...gate.props("add a client")}
          >
            Add client
          </Button>
        ) : (
          <Link
            to={ROUTES.addClient}
            className={buttonClasses({ size: "md" })}
          >
            <Plus className="w-4 h-4" />
            Add client
          </Link>
        ),
      };

    // A draft is something the builder was left in the middle of, so an empty
    // Draft tab is good news rather than a gap to be filled — it gets no
    // button, only the reassurance that nothing is sitting half-finished.
    if (activeTab === "draft")
      return {
        title: "No draft plans",
        description:
          "Plans you start but do not activate wait here. Nothing is half-finished right now.",
      };

    // Clients on the roster and nothing written for them yet: the same welcome,
    // but the first step is here rather than on another screen, so the button
    // opens the builder instead of leading away.
    return firstPlanState();
  };

  const handleCreate = async (input: NewPlan) => {
    try {
      const plan = await create(input);
      setCreating(false);
      // Straight into the builder: a plan with no meals on it is not
      // finished, and the dialog only collected enough to open one.
      navigate(planDetailPath(plan.id));
    } catch (reason) {
      // The dialog stays open on its answers, so the create can be tried
      // again without typing them all a second time.
      toast.error(
        "Could not create the plan",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    }
  };

  /**
   * Sends the plan to the client.
   *
   * The record the backend answers with is written back over the browser's
   * copy, which is what turns the row's pill from Draft to Active — and, via
   * `savePlan`, marks it as one this sitting has changed, so the row the list
   * fetched does not overwrite it.
   */
  const handleActivate = async () => {
    if (!activating || activatingNow) return;

    setActivatingNow(true);
    try {
      const record = await activateMealPlan(getToken, activating.id);
      savePlan(record);
      setActivating(null);
      toast.success(
        "Plan activated",
        `${activating.clientName}'s plan is now with them.`
      );
    } catch (reason) {
      toast.error(
        "Could not activate the plan",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    } finally {
      setActivatingNow(false);
    }
  };

  const handleDelete = () => {
    if (!deleting) return;
    remove(deleting.id);
    setDeleting(null);
    toast.success("Plan deleted", `${deleting.clientName}'s plan was removed.`);
  };

  return (
    <section className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b border-line px-4 pt-4 sm:px-6 sm:pt-5">
        <Tabs
          tabs={TABS}
          activeTab={activeTab}
          onChange={(tab) => {
            setActiveTab(tab);
            setSearch("");
            setPage(1);
          }}
        />

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-3">
          <div className="relative w-full sm:w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search"
              aria-label="Search plans"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>

          {/* Gone rather than disabled while there is nobody to plan for: the
              empty state below is already saying what to do instead, and a
              greyed-out button beside it would be a second way to ask the same
              question and get the same no. It comes back with the first
              client. */}
          {!noClientsYet && (
            <Button
              size="md"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => setCreating(true)}
              {...gate.props("create a plan")}
            >
              Create plan
            </Button>
          )}
        </div>
      </div>

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it: this line is not seen, so it costs nothing to be
          early, and the skeleton rows it stands for are hidden from screen
          readers — they are the shape of an answer rather than the answer. */}
      {loadStatus === "loading" && (
        <p role="status" className="sr-only">
          Loading plans…
        </p>
      )}

      <PlansTable
        plans={visible}
        startIndex={startIndex}
        phase={phase}
        empty={emptyState()}
        // The rows stay readable and stop being doors: the builder is what a
        // subscription buys, so with none running a plan does not open. The
        // menu beside each row goes the same way. See `PlansTable`.
        gate={gate}
        onSelectPlan={(plan) => navigate(planDetailPath(plan.id))}
        onDeletePlan={setDeleting}
        onActivatePlan={setActivating}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        resultCount={visible.length}
      />

      {creating && (
        <CreatePlanModal
          onClose={() => setCreating(false)}
          onCreate={handleCreate}
        />
      )}

      <Modal
        open={!!activating}
        title="Activate plan"
        onClose={() => setActivating(null)}
        className="max-w-[440px]"
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              disabled={activatingNow}
              onClick={() => setActivating(null)}
            >
              Cancel
            </Button>
            <Button
              size="md"
              disabled={activatingNow}
              onClick={handleActivate}
            >
              {activatingNow ? "Activating…" : "Activate"}
            </Button>
          </>
        }
      >
        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          {activating?.clientName}&rsquo;s plan will go to them and stop being
          a draft.
        </p>
      </Modal>

      <Modal
        open={!!deleting}
        title="Delete plan"
        onClose={() => setDeleting(null)}
        className="max-w-[440px]"
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setDeleting(null)}
            >
              Cancel
            </Button>
            <Button variant="danger" size="md" onClick={handleDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          {deleting?.clientName}&rsquo;s plan will be removed. This cannot be
          undone.
        </p>
      </Modal>
    </section>
  );
};

export default MealPlansPanel;
